/** Presentation only: an endless gearbox over a wrapped Shepard octave, no drivetrain changes. */
export class VirtualGear {
  phase = 0;
  gear = 1;
  shifts = 0;
  private loadTime = 0;
  private brakeTime = 0;
  private cooldown = 0;
  private shiftAge = 1;
  private shiftDirection = 0;
  private previousRatio: number | null = null;
  reset(): void {
    this.phase = 0;
    this.gear = 1;
    this.shifts = 0;
    this.loadTime = 0;
    this.brakeTime = 0;
    this.cooldown = 0;
    this.shiftAge = 1;
    this.previousRatio = null;
  }
  step(
    speedRatio: number,
    command: number,
    dt: number,
    active = true,
  ): { phase: number; gain: number; shift: number } {
    if (!active || dt <= 0)
      return { phase: this.phase, gain: this.envelope(), shift: 0 };
    dt = Math.min(0.1, dt);
    const ratio = Math.max(0, Math.min(1, speedRatio));
    const decelerating =
      command < -0.05 ||
      (this.previousRatio !== null && ratio < this.previousRatio - dt * 0.02);
    this.previousRatio = ratio;
    this.cooldown = Math.max(0, this.cooldown - dt);
    // Perceptual revolutions rise under load even at top speed; coasting follows actual speed.
    const rate = decelerating
      ? -(0.09 + 0.1 * ratio)
      : command > 0.1 && ratio > 0.035
        ? 0.105 + 0.055 * ratio
        : 0;
    this.phase += rate * dt;
    this.loadTime =
      command > 0.25 && !decelerating && ratio > 0.1
        ? this.loadTime + dt
        : Math.max(0, this.loadTime - dt);
    this.brakeTime = decelerating && ratio > 0.08 ? this.brakeTime + dt : 0;
    let shift = 0;
    if (this.cooldown === 0 && this.loadTime >= 4) {
      shift = -1;
      this.gear++;
    } else if (this.cooldown === 0 && this.brakeTime >= 1.3 && this.gear > 1) {
      shift = 1;
      this.gear--;
    }
    if (shift) {
      this.shiftDirection = shift;
      this.shiftAge = 0;
      this.shifts++;
      this.cooldown = 1.5;
      this.loadTime = 0;
      this.brakeTime = 0;
    }
    // A fifth is log2(3/2) octaves. Integrate a short smooth ramp, avoiding a click.
    const before = Math.min(1, this.shiftAge / 0.18);
    this.shiftAge += dt;
    const after = Math.min(1, this.shiftAge / 0.18);
    const ease = (t: number) => t * t * (3 - 2 * t);
    this.phase +=
      this.shiftDirection * Math.log2(1.5) * (ease(after) - ease(before));
    this.phase = ((this.phase % 1) + 1) % 1;
    if (ratio < 0.025 && command <= 0) {
      this.gear = 1;
      this.loadTime = 0;
      this.brakeTime = 0;
    }
    return { phase: this.phase, gain: this.envelope(), shift };
  }
  private envelope(): number {
    return this.shiftAge >= 0.25
      ? 1
      : 1 - 0.42 * Math.sin((Math.PI * this.shiftAge) / 0.25);
  }
}
